import { normalizeStringArray } from './compatibilityOptions';

export const PIN_CAPABILITY_COLORS = Object.freeze({
  gpio: '#3b82f6',
  uart: '#22c55e',
  i2c: '#a855f7',
  spi: '#f97316',
  power: '#ff6b6b',
  ground: '#000000',
  pwm: '#facc15',
  analog: '#14b8a6',
  control: '#ec4899',
  dac: '#14b8a6',
  special: '#ec4899',
});

// Public board-detail filters are capabilities, not the legacy pin categories.
export const PIN_FILTER_GROUPS = [
  { value: 'gpio', label: 'GPIO', color: PIN_CAPABILITY_COLORS.gpio },
  { value: 'uart', label: 'UART', color: PIN_CAPABILITY_COLORS.uart },
  { value: 'i2c', label: 'I2C', color: PIN_CAPABILITY_COLORS.i2c },
  { value: 'spi', label: 'SPI', color: PIN_CAPABILITY_COLORS.spi },
  { value: 'analog', label: 'Analog', color: PIN_CAPABILITY_COLORS.analog },
  { value: 'power', label: 'Power', color: PIN_CAPABILITY_COLORS.power },
  { value: 'ground', label: 'Ground', color: PIN_CAPABILITY_COLORS.ground },
  { value: 'pwm', label: 'PWM', color: PIN_CAPABILITY_COLORS.pwm },
  { value: 'control', label: 'Control', color: PIN_CAPABILITY_COLORS.control },
];

const PIN_FILTER_GROUP_MAP = new Map(PIN_FILTER_GROUPS.map((group) => [group.value, group]));
const PIN_FILTER_GROUP_HINTS = {
  gpio: [/\bgpio\b/, /\bdigital\b/, /\bpio\b/, /\blogic\b/],
  uart: [/\buart\b/, /\bserial\b/, /\b(?:tx|rx)\d*\b/],
  i2c: [/\bi2c\b/, /\biic\b/, /\b(?:sda|scl)\d*\b/],
  spi: [/\bspi\b/, /\b(?:mosi|miso|sck|sclk|chip[ -]?select)\b/],
  analog: [/\banalog\b/, /\badc\b/, /\bdac\b/],
  power: [/\bpower\b/, /\bvcc\b/, /\bvdd\b/, /\bvin\b/, /\b3v3\b/, /\b3\.3v\b/, /\b5v\b/],
  ground: [/\bground\b/, /\bgnd\b/],
  pwm: [/\bpwm\b/],
  control: [/\bcontrol\b/, /\breset\b/, /\benable\b/, /\bboot\b/, /\bclock\b/, /\binterrupt\b/],
};

const CAPABILITY_TAG_LABELS = {
  gpio: 'GPIO', pwm: 'PWM', analog: 'Analog', 'analog-in': 'Analog Input',
  'analog-input': 'Analog Input', 'analog-out': 'Analog Output', 'analog-output': 'Analog Output',
  adc: 'ADC', dac: 'DAC', interrupt: 'Interrupt', ground: 'Ground', gnd: 'Ground', power: 'Power',
  'power-3v3': 'Power 3.3V', 'power-5v': 'Power 5V', 'power-vin': 'Power VIN',
  'power-input': 'Power Input', 'power-output': 'Power Output',
  'power-output-3v3': '3.3V Output', 'power-output-5v': '5V Output',
  i2c: 'I2C', 'i2c-sda': 'I2C SDA', 'i2c-scl': 'I2C SCL',
  spi: 'SPI', 'spi-mosi': 'SPI MOSI', 'spi-miso': 'SPI MISO', 'spi-sck': 'SPI SCK', 'spi-cs': 'SPI CS',
  uart: 'UART', 'uart-tx': 'UART TX', 'uart-rx': 'UART RX',
  'one-wire': '1-Wire', '1-wire': '1-Wire', can: 'CAN', i2s: 'I2S', usb: 'USB', sdio: 'SDIO',
  ble: 'BLE', wifi: 'WiFi', pio: 'PIO', 'chip-select': 'Chip Select', 'chip-enable': 'Chip Enable',
  control: 'Control', enable: 'Enable', reset: 'Reset', clock: 'Clock',
};
const CAPABILITY_TAG_TOKEN_LABELS = {
  gpio: 'GPIO', pwm: 'PWM', adc: 'ADC', dac: 'DAC', i2c: 'I2C', spi: 'SPI', uart: 'UART',
  tx: 'TX', rx: 'RX', sda: 'SDA', scl: 'SCL', mosi: 'MOSI', miso: 'MISO', sck: 'SCK', cs: 'CS',
  vin: 'VIN', can: 'CAN', i2s: 'I2S', usb: 'USB', sdio: 'SDIO', ble: 'BLE', wifi: 'WiFi', pio: 'PIO',
  '3v3': '3.3V', '5v': '5V',
};

const GPIO_TAGS = new Set([
  'gpio', 'digital', 'digital-in', 'digital-input', 'digital-out', 'digital-output',
  'pio', 'open-drain', 'open-collector',
]);
const GROUND_TAGS = new Set(['ground', 'gnd']);
const POWER_TAGS = new Set([
  'power', 'vcc', 'vdd', 'vin', '3v3', '3.3v', '5v', 'power-input', 'power-output',
  'power-3v3', 'power-5v', 'power-vin', 'power-output-3v3', 'power-output-5v',
]);
const CONTROL_TAGS = new Set([
  'interrupt', 'chip-select', 'chip-enable', 'control', 'enable', 'reset', 'clock', 'boot',
  'strapping', 'touch', 'touch-input', 'active-low', 'active-high',
]);

export const normalizeCapabilityTag = (value) => String(value ?? '').trim().toLowerCase();

export const formatCapabilityTagLabel = (value) => {
  const normalizedValue = normalizeCapabilityTag(value);
  if (!normalizedValue) return '';
  if (CAPABILITY_TAG_LABELS[normalizedValue]) return CAPABILITY_TAG_LABELS[normalizedValue];
  return normalizedValue.split('-').filter(Boolean).map((token) => (
    CAPABILITY_TAG_TOKEN_LABELS[token] || `${token.charAt(0).toUpperCase()}${token.slice(1)}`
  )).join(' ');
};

export const getPinCapabilityTags = (pin) => (
  normalizeStringArray(pin?.capability_tags ?? pin?.capabilityTags)
    .map(normalizeCapabilityTag)
    .filter((tag, index, tags) => tag && tags.indexOf(tag) === index)
);

export const normalizePinFilterGroup = (value) => {
  const normalizedValue = String(value ?? '').trim().toLowerCase();
  return PIN_FILTER_GROUP_MAP.has(normalizedValue) ? normalizedValue : '';
};

export const getPinFilterGroupDefinition = (value) => (
  PIN_FILTER_GROUP_MAP.get(normalizePinFilterGroup(value)) || null
);

export const inferPinFilterGroup = (value) => {
  const normalizedValue = String(value ?? '').trim().toLowerCase();
  const exactGroup = normalizePinFilterGroup(normalizedValue);
  if (exactGroup || !normalizedValue) return exactGroup;
  const match = Object.entries(PIN_FILTER_GROUP_HINTS).find(([, patterns]) => (
    patterns.some((pattern) => pattern.test(normalizedValue))
  ));
  return match?.[0] || '';
};

export const normalizePinFilterGroupList = (values) => (
  Array.isArray(values)
    ? values.map(normalizePinFilterGroup).filter((value, index, list) => value && list.indexOf(value) === index)
    : []
);

const hasTagPrefix = (tag, prefix) => tag === prefix || tag.startsWith(`${prefix}-`);

export const getPinFilterGroupsFromTags = (capabilityTags) => {
  const groups = new Set();
  normalizeStringArray(capabilityTags).map(normalizeCapabilityTag).filter(Boolean).forEach((tag) => {
    if (GPIO_TAGS.has(tag) || hasTagPrefix(tag, 'digital')) groups.add('gpio');
    if (hasTagPrefix(tag, 'uart')) groups.add('uart');
    if (hasTagPrefix(tag, 'i2c')) groups.add('i2c');
    if (hasTagPrefix(tag, 'spi')) groups.add('spi');
    if (hasTagPrefix(tag, 'analog') || tag === 'adc' || tag === 'dac' || tag.endsWith('-analog')) groups.add('analog');
    if (POWER_TAGS.has(tag) || hasTagPrefix(tag, 'power')) groups.add('power');
    if (GROUND_TAGS.has(tag)) groups.add('ground');
    if (hasTagPrefix(tag, 'pwm') || tag.endsWith('-pwm')) groups.add('pwm');
    if (CONTROL_TAGS.has(tag)) groups.add('control');
  });
  return PIN_FILTER_GROUPS.map(({ value }) => value).filter((value) => groups.has(value));
};

export const getPinFilterGroups = (pin) => getPinFilterGroupsFromTags(getPinCapabilityTags(pin));

const normalizePinsCollection = (pins) => (
  Array.isArray(pins) ? pins : (Array.isArray(pins?.data) ? pins.data : [])
);

export const getPresentPinFilterGroupOptions = (pins = []) => {
  const presentGroups = new Set();
  normalizePinsCollection(pins).forEach((pin) => {
    getPinFilterGroups(pin).forEach((group) => presentGroups.add(group));
  });
  return PIN_FILTER_GROUPS.filter(({ value }) => presentGroups.has(value));
};

export const getPresentPinFilterGroupOptionsForValues = (values = []) => {
  const presentGroups = new Set(normalizeStringArray(values).map(inferPinFilterGroup).filter(Boolean));
  return PIN_FILTER_GROUPS.filter(({ value }) => presentGroups.has(value));
};

export const getPinFilterMatchState = (pin, activeFilterGroups = []) => {
  const pinGroups = getPinFilterGroups(pin);
  const activeGroups = normalizePinFilterGroupList(activeFilterGroups);
  const matched = PIN_FILTER_GROUPS.find(({ value }) => activeGroups.includes(value) && pinGroups.includes(value)) || null;
  const fallback = PIN_FILTER_GROUPS.find(({ value }) => pinGroups.includes(value)) || null;
  const resolved = matched || fallback;
  return {
    filterGroups: pinGroups,
    isActive: activeGroups.length === 0 || Boolean(matched),
    matchedGroup: matched?.value || '',
    color: resolved?.color || PIN_CAPABILITY_COLORS.special,
    label: resolved?.label || 'Special',
  };
};
